/**
 * @typedef {{
 *    preferNative: boolean,
 *    lazyClass: string,
 *    offset: number,
 *    useOffsetAfterInteractive: boolean
 * }} Options
 * 
 * preferNative - do nothing if the browser supports loading="lazy" attribute 
 * and the img has that attribute
 */

import { swapImgSources } from '../utils/base';

/** @type {Options} */
const DEF_OPTIONS = {
   lazyClass: 'lazy',
   preferNative: true,
   offset: window.innerHeight,
   scrollableContainer: document,
   useOffsetAfterInteractive: true,
};

const hasNativeSupport = 'loading' in HTMLImageElement.prototype;

export default class ImgLazyLoad {
   /**
    * @param {HTMLCollection | HTMLImageElement[]} images 
    * @param {Options} options
    */
   constructor(images, options = {}) {
      if (!images) return;

      this._runHandler = () => this._run();
      this._active = false;
      this._frameId = 0;

      this._options = { ...DEF_OPTIONS, ...options };
      this._images = [...images];

      if (this._options.useOffsetAfterInteractive) {
         this._setupOffsetAfterInteractive(this._options.offset);
         this._options.offset = 0;
      }

      if (this._options.preferNative && hasNativeSupport) {
         this._initNativeLazy();
      }

      if (!this._images.length) {
         return;
      }

      this._addEvents();
      this._run();
   }

   _run() {
      if (this._active) return;
      this._active = true;

      if (this._frameId) {
         cancelAnimationFrame(this._frameId);
      }

      this._frameId = requestAnimationFrame(() => {
         this._images.forEach((img) => {
            this._runForImg(img);
         });

         this._active = false;

         if (!this._images.length) {
            this._removeEvents();
         }
      });
   }

   _runForImg(img) {
      const bounding = img.getBoundingClientRect();
      const offset = this._options.offset;

      const isVisible = bounding.bottom >= -offset &&
         bounding.top <= window.innerHeight + offset;

      if (!isVisible) {
         return;
      }

      img.classList.remove(this._options.lazyClass);

      this._swapImgSources(img);
      this._forgetImg(img);
   }

   _swapImgSources(img) {
      swapImgSources(img);
   }

   _forgetImg(img) {
      this._images = this._images.filter(lazyImg => lazyImg !== img);
   }

   _setupOffsetAfterInteractive(realOffset) {
      const runHandler = this._runHandler;

      this._runHandler = (...args) => {
         runHandler(...args);

         // Remove custom runHandler from events
         this._removeEvents();

         this._options.offset = realOffset;
         this._runHandler = runHandler;

         this._addEvents();
      };
   }

   _initNativeLazy() {
      this._images = this._images.filter((img) => {
         const useNative = img.hasAttribute('loading');
         if (useNative) this._swapImgSources(img);
         return !useNative;
      });
   }

   _addEvents() {
      this._options.scrollableContainer.addEventListener('scroll', this._runHandler);
      window.addEventListener('resize', this._runHandler);
      window.addEventListener('orientationchange', this._runHandler);
   }

   _removeEvents() {
      this._options.scrollableContainer.removeEventListener('scroll', this._runHandler);
      window.removeEventListener('resize', this._runHandler);
      window.removeEventListener('orientationchange', this._runHandler);
   }
}
